import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { showPromptDialog } from '../components/ConfirmDialog.js';
import { flashToast } from '../util/toast.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { formatCrowns, formatBigNum } from '../util/formatBig.js';
import type {
  RacingYardResponse,
  RacingRankingsResponse,
  StableDto,
  HorseDto,
  RaceTrackDto,
  ActiveRaceDto,
  HorseGearDto,
  RaceBreedDto,
  ClaimRaceResponse,
  HorseMarketResponse,
  HorseMarketEntryDto,
} from '@rex-mortis/shared';

type Tab = 'stable' | 'races' | 'rankings' | 'market';

interface FlashState { kind: 'success' | 'danger' | 'warn'; message: string; }

export async function renderRacing(root: HTMLElement): Promise<void> {
  let yard: RacingYardResponse | null = null;
  let rankings: RacingRankingsResponse | null = null;
  let market: HorseMarketResponse | null = null;
  let tab: Tab = 'stable';
  let flash: FlashState | null = null;
  let lastResult: ClaimRaceResponse | null = null;
  // Which horse's gear panel is expanded (id) — kept across re-renders.
  let openGearHorseId: string | null = null;

  async function refresh(): Promise<void> {
    try {
      const me = await api.me();
      store.set({ account: me.account, character: me.character });
      yard = await api.getRacingYard();
    } catch (err) {
      flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load the Racing Yard.' };
    }
  }

  async function loadRankings(): Promise<void> {
    try {
      rankings = await api.getRacingRankings();
    } catch {
      rankings = { topRiders: [], topHorses: [] };
    }
  }

  async function loadMarket(): Promise<void> {
    try {
      market = await api.getHorseMarket();
    } catch {
      market = { listings: [], canAfford: '0' };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const rerender = () => anchored(renderMain);

    function renderMain(): void {
      const char = store.get().character;
      if (!char || !yard) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/racing.webp" alt=""
             onerror="this.style.display='none'" />
        <h1>The Racing Yard</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top:-8px;">
          Raise champions, race the realm's ten courses, and chase Racing Prestige.
        </p>

        <div id="flash"></div>
        <div class="rc-tabs">
          <button class="rc-tab ${tab === 'stable' ? 'active' : ''}" data-tab="stable">Stable</button>
          <button class="rc-tab ${tab === 'races' ? 'active' : ''}" data-tab="races">Races</button>
          <button class="rc-tab ${tab === 'market' ? 'active' : ''}" data-tab="market">Market</button>
          <button class="rc-tab ${tab === 'rankings' ? 'active' : ''}" data-tab="rankings">Rankings</button>
        </div>
        <div id="rc-body"></div>
      `;
      main.appendChild(buildStyle());

      if (flash) { flashToast(flash); flash = null; }

      main.querySelectorAll<HTMLButtonElement>('.rc-tab').forEach((b) => {
        b.addEventListener('click', async () => {
          tab = b.dataset.tab as Tab;
          if (tab === 'rankings' && !rankings) await loadRankings();
          if (tab === 'market') await loadMarket();
          rerender();
        });
      });

      const body = main.querySelector<HTMLDivElement>('#rc-body')!;
      if (tab === 'stable') renderStableTab(body);
      else if (tab === 'races') renderRacesTab(body);
      else if (tab === 'market') renderMarketTab(body);
      else renderRankingsTab(body);
    }

    // ── STABLE ────────────────────────────────────────────────────────────────
    function renderStableTab(body: HTMLDivElement): void {
      const s = yard!.stable;
      const slotBtn = s.nextSlotCost === null
        ? `<span class="pill">Stable at max (${s.maxStableSlots})</span>`
        : `<button class="btn ghost" id="buy-slot">Add a stall — ${formatCrowns(s.nextSlotCost)} crowns</button>`;

      body.innerHTML = `
        ${lastResult ? renderResultBanner(lastResult) : ''}
        <section class="panel">
          <div style="display:flex; flex-wrap:wrap; gap:var(--s-3); align-items:center; justify-content:space-between;">
            <div>
              <span class="pill">Stalls: ${s.usedSlots}/${s.stableSlots}</span>
              <span class="pill gold">Racing Prestige: ${s.racingPrestige}</span>
            </div>
            ${slotBtn}
          </div>
        </section>

        <h2 class="rc-h2">Your Horses</h2>
        <div id="my-horses">${s.horses.length === 0
          ? '<p style="color:var(--c-ink-soft);">Your stable is empty. Buy your first horse below.</p>'
          : `<div class="rc-grid">${s.horses.map((h) => horseCard(h)).join('')}</div>`}</div>

        <h2 class="rc-h2">The Stablemaster</h2>
        <p style="color:var(--c-ink-soft); font-size:0.9rem; margin-top:-4px;">Horses you can buy. Better breeds need Racing Prestige and character level.</p>
        <div class="rc-grid">${yard!.breeds.map((b) => breedCard(b, s)).join('')}</div>
      `;

      // Buy slot
      body.querySelector<HTMLButtonElement>('#buy-slot')?.addEventListener('click', async () => {
        const cost = s.nextSlotCost!;
        if (!(await showConfirmDialog({ title: 'Buy a new stall?', message: `This costs ${formatCrowns(cost)} crowns.`, confirmLabel: 'Buy stall' }))) return;
        await act(() => api.buyStableSlot(), 'New stall built.');
      });

      // Buy horse
      body.querySelectorAll<HTMLButtonElement>('button[data-buy]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.buy!;
          const breed = yard!.breeds.find((b) => b.slug === slug)!;
          if (!(await showConfirmDialog({ title: `Buy ${breed.name}?`, message: `This costs ${formatCrowns(breed.cost)} crowns.`, confirmLabel: 'Buy horse' }))) return;
          await act(() => api.buyHorse(slug), `${breed.name} joins your stable.`);
        });
      });

      // Rename
      body.querySelectorAll<HTMLButtonElement>('button[data-rename]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.rename!;
          const cur = yard!.stable.horses.find((h) => h.id === id);
          const name = await showPromptDialog({
            title: 'Name your horse',
            message: 'Name your horse (2-24 characters):',
            defaultValue: cur?.name ?? '',
            inputType: 'text',
            confirmLabel: 'Rename',
            tone: 'gold',
          });
          if (!name) return;
          await act(() => api.renameHorse(id, name.trim()), 'Horse renamed.');
        });
      });

      // Toggle gear panel
      body.querySelectorAll<HTMLButtonElement>('button[data-gear]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.gear!;
          openGearHorseId = openGearHorseId === id ? null : id;
          rerender();
        });
      });

      // Forge & fit gear
      body.querySelectorAll<HTMLButtonElement>('button[data-fit]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const [horseId, gearSlug] = btn.dataset.fit!.split('|');
          await act(() => api.fitHorseGear(horseId!, gearSlug!), 'Gear forged and fitted.');
        });
      });

      // Sell to the stablemaster (NPC merchant)
      body.querySelectorAll<HTMLButtonElement>('button[data-sell]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.sell!;
          const h = yard!.stable.horses.find((x) => x.id === id);
          if (!h) return;
          const bid = formatCrowns(Number(h.merchantBid));
          if (!(await showConfirmDialog({
            title: `Sell ${escapeHtml(h.name)}?`,
            message: `The stablemaster will pay ${bid} crowns for ${escapeHtml(h.name)} (and any fitted gear). This cannot be undone.`,
            confirmLabel: 'Sell horse',
            tone: 'danger',
          }))) return;
          await act(() => api.sellHorseToMerchant(id), `Sold ${h.name} for ${bid} crowns.`);
        });
      });

      // List on the player market
      body.querySelectorAll<HTMLButtonElement>('button[data-list]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.list!;
          const h = yard!.stable.horses.find((x) => x.id === id);
          if (!h) return;
          const raw = await showPromptDialog({
            title: `List ${escapeHtml(h.name)} for sale`,
            message: 'Set an asking price in crowns. The horse (with its fitted gear) transfers to the buyer. A 5% market fee is taken from your proceeds.',
            defaultValue: h.merchantBid,
            inputType: 'text',
            confirmLabel: 'List horse',
            tone: 'gold',
          });
          if (!raw) return;
          const digits = raw.replace(/[^\d]/gu, '');
          if (!digits || Number(digits) <= 0) {
            flash = { kind: 'warn', message: 'Enter a whole number of crowns.' };
            rerender();
            return;
          }
          await act(() => api.listHorse(id, digits), `${h.name} listed for ${formatCrowns(Number(digits))} crowns.`);
        });
      });

      // Take off the market
      body.querySelectorAll<HTMLButtonElement>('button[data-unlist]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.unlist!;
          await act(() => api.unlistHorse(id), 'Horse taken off the market.');
        });
      });
    }

    // ── MARKET (player-to-player horse sales) ──────────────────────────────────
    function renderMarketTab(body: HTMLDivElement): void {
      const m = market;
      if (!m) { body.innerHTML = '<p>Loading the market…</p>'; return; }
      const s = yard!.stable;
      const stableFull = s.usedSlots >= s.stableSlots;

      body.innerHTML = `
        <section class="panel">
          <p style="margin:0; color:var(--c-ink-soft); font-size:0.9rem;">
            Horses other riders have put up for sale. Buying one transfers it — fitted gear and all — to a free stall in your stable.
            ${stableFull ? '<strong style="color:var(--c-danger);"> Your stable is full — free a stall before buying.</strong>' : ''}
          </p>
        </section>
        <h2 class="rc-h2">The Horse Market</h2>
        ${m.listings.length === 0
          ? '<p style="color:var(--c-ink-soft);">No horses are for sale right now. Check back later.</p>'
          : `<div class="rc-grid">${m.listings.map((e) => marketCard(e, m.canAfford, stableFull)).join('')}</div>`}
      `;

      body.querySelectorAll<HTMLButtonElement>('button[data-buy-listing]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.buyListing!;
          const entry = m.listings.find((e) => e.listingId === id);
          if (!entry) return;
          if (!(await showConfirmDialog({
            title: `Buy ${escapeHtml(entry.horse.name)}?`,
            message: `Pay ${formatCrowns(Number(entry.price))} crowns to ${escapeHtml(entry.sellerName)} for ${escapeHtml(entry.horse.name)} (${escapeHtml(entry.horse.breedName)}, Lv ${entry.horse.level}) and its fitted gear.`,
            confirmLabel: 'Buy horse',
            tone: 'gold',
          }))) return;
          try {
            await api.buyHorseListing(id);
            flash = { kind: 'success', message: `${entry.horse.name} is now in your stable.` };
            await loadMarket();
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Purchase failed.' };
            await loadMarket();
          } finally {
            rerender();
          }
        });
      });
    }

    function marketCard(e: HorseMarketEntryDto, canAfford: string, stableFull: boolean): string {
      const h = e.horse;
      const afford = BigInt(canAfford) >= BigInt(e.price);
      const disabled = e.isOwn || !afford || stableFull;
      const label = e.isOwn ? 'Your listing'
        : !afford ? 'Not enough crowns'
        : stableFull ? 'Stable full'
        : `Buy — ${formatCrowns(Number(e.price))}`;
      return `
        <div class="rc-card">
          <img class="rc-art" src="/img/horses/${h.horseSlug}.webp" alt="" onerror="this.remove()" />
          <div style="display:flex; justify-content:space-between; align-items:baseline; gap:6px;">
            <h3 style="margin:0;">${escapeHtml(h.name)}</h3>
            <span class="tag">${h.tierLabel}</span>
          </div>
          <div style="color:var(--c-ink-soft); font-size:0.82rem;">${escapeHtml(h.breedName)} · Lv ${h.level} · ${h.wins}W / ${h.races}R</div>
          <div class="rc-tags">
            <span class="tag">${cap(h.surface)}</span>
            <span class="tag">${cap(h.distance)}</span>
          </div>
          ${statBlock(h.stats)}
          <div class="rc-gearline">${gearLine(h)}</div>
          <div style="font-size:0.8rem; color:var(--c-ink-soft); margin-top:4px;">Seller: ${escapeHtml(e.sellerName)}</div>
          <div style="margin-top:auto;">
            <button class="btn" data-buy-listing="${e.listingId}" ${disabled ? 'disabled' : ''}>${label}</button>
          </div>
        </div>
      `;
    }

    function horseCard(h: HorseDto): string {
      const cd = h.cooldownRemaining > 0;
      const statusPill = h.listed
        ? '<span class="pill gold">For sale</span>'
        : h.racing
          ? '<span class="pill warn">Racing</span>'
          : cd
            ? `<span class="pill warn">Resting ${formatRemaining(h.cooldownRemaining)}</span>`
            : '<span class="pill success">Ready</span>';
      const actions = h.listed
        ? `<div style="display:flex; flex-direction:column; gap:6px; margin-top:auto;">
             <div style="font-size:0.82rem; color:var(--c-ink-soft);">Listed on the market for <strong>${formatCrowns(Number(h.listPrice))}</strong> crowns.</div>
             <button class="btn sm ghost" data-unlist="${h.id}">Take off market</button>
           </div>`
        : `<div style="display:flex; gap:6px; flex-wrap:wrap; margin-top:auto;">
             <button class="btn sm" data-rename="${h.id}">Rename</button>
             <button class="btn sm ghost" data-gear="${h.id}">${openGearHorseId === h.id ? 'Hide gear' : 'Fit gear'}</button>
             <button class="btn sm ghost" data-sell="${h.id}" ${h.racing ? 'disabled' : ''} title="Sell to the stablemaster">Sell · ${formatCrowns(Number(h.merchantBid))}</button>
             <button class="btn sm ghost" data-list="${h.id}" ${h.racing ? 'disabled' : ''} title="List on the player market">List for sale</button>
           </div>`;
      return `
        <div class="rc-card">
          <img class="rc-art" src="/img/horses/${h.horseSlug}.webp" alt="" onerror="this.remove()" />
          <div style="display:flex; justify-content:space-between; align-items:baseline; gap:6px;">
            <h3 style="margin:0;">${escapeHtml(h.name)}</h3>
            ${statusPill}
          </div>
          <div style="color:var(--c-ink-soft); font-size:0.82rem;">${escapeHtml(h.breedName)} · ${h.tierLabel} · Lv ${h.level}</div>
          <div class="rc-tags">
            <span class="tag">${cap(h.surface)}</span>
            <span class="tag">${cap(h.distance)}</span>
            <span class="tag">${h.wins}W / ${h.races}R</span>
          </div>
          ${statBlock(h.stats)}
          ${xpBar(h)}
          <div class="rc-gearline">${gearLine(h)}</div>
          ${actions}
          ${openGearHorseId === h.id && !h.listed ? gearPanel(h) : ''}
        </div>
      `;
    }

    function gearPanel(h: HorseDto): string {
      const slots: Array<{ key: 'saddle' | 'shoes' | 'barding' | 'charm'; label: string }> = [
        { key: 'saddle', label: 'Saddle' }, { key: 'shoes', label: 'Shoes' },
        { key: 'barding', label: 'Barding' }, { key: 'charm', label: 'Charm' },
      ];
      return `
        <div class="rc-gearpanel">
          ${slots.map((sl) => {
            const fitted = h.gear[sl.key];
            const options = yard!.gear.filter((g) => g.slot === sl.key);
            return `
              <div class="rc-gearslot">
                <div class="rc-gearslot-h"><img class="rc-gear-emblem" src="/img/horse-gear/${sl.key}.webp" alt="" onerror="this.remove()" />${sl.label}${fitted ? ` — <em>${escapeHtml(fitted.name)}</em>` : ' — <span style="color:var(--c-ink-soft);">empty</span>'}</div>
                <div class="rc-gearopts">
                  ${options.map((g) => gearOptionRow(h.id, g)).join('')}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `;
    }

    function gearOptionRow(horseId: string, g: HorseGearDto): string {
      const bonus = Object.entries(g.bonus).map(([k, v]) => `+${v} ${shortStat(k)}`).join(', ');
      const mats = g.materials.map((m) => `${m.name} ${m.have}/${m.qty}`).join(', ');
      return `
        <div class="rc-gearopt ${g.forgeable ? '' : 'blocked'}">
          <div>
            <strong>${escapeHtml(g.name)}</strong> <span class="tag">${g.tierLabel}</span>
            <div style="font-size:0.78rem; color:var(--c-ink-soft);">${bonus} · ${formatCrowns(g.crowns)} crowns · ${escapeHtml(mats)}</div>
          </div>
          <button class="btn sm" data-fit="${horseId}|${g.slug}" ${g.forgeable ? '' : 'disabled'}
            title="${g.blockedReason ? escapeHtml(g.blockedReason) : 'Forge from materials and fit'}">
            Forge &amp; Fit
          </button>
        </div>
      `;
    }

    function breedCard(b: RaceBreedDto, s: StableDto): string {
      const lockLevel = (store.get().character?.level ?? 1) < b.levelGate;
      const lockPrestige = s.racingPrestige < b.prestigeRequired;
      const full = s.usedSlots >= s.stableSlots;
      const locked = lockLevel || lockPrestige || full;
      const reason = lockLevel ? `Needs level ${b.levelGate}`
        : lockPrestige ? `Needs ${b.prestigeRequired} prestige`
        : full ? 'Stable full' : '';
      return `
        <div class="rc-card ${locked ? 'locked' : ''}">
          <img class="rc-art" src="/img/horses/${b.slug}.webp" alt="" onerror="this.remove()" />
          <div style="display:flex; justify-content:space-between; align-items:baseline; gap:6px;">
            <h3 style="margin:0;">${escapeHtml(b.name)}</h3>
            <span class="tag">${b.tierLabel}</span>
          </div>
          <p style="font-style:italic; color:var(--c-ink-soft); font-size:0.84rem; margin:4px 0;">"${escapeHtml(b.blurb)}"</p>
          <div class="rc-tags">
            <span class="tag">${cap(b.surface)}</span>
            <span class="tag">${cap(b.distance)}</span>
          </div>
          ${statBlock(b.stats)}
          <div style="margin-top:auto;">
            <button class="btn" data-buy="${b.slug}" ${locked ? 'disabled' : ''}>
              ${locked ? reason : `Buy — ${formatCrowns(b.cost)} crowns`}
            </button>
          </div>
        </div>
      `;
    }

    // ── RACES ───────────────────────────────────────────────────────────────
    function renderRacesTab(body: HTMLDivElement): void {
      const s = yard!.stable;
      const available = s.horses.filter((h) => !h.racing && h.cooldownRemaining <= 0);

      body.innerHTML = `
        ${lastResult ? renderResultBanner(lastResult) : ''}
        ${yard!.active.length ? `
          <h2 class="rc-h2">In Progress</h2>
          <div id="active-races">${yard!.active.map((a) => activeRaceCard(a)).join('')}</div>
        ` : ''}
        <h2 class="rc-h2">The Courses</h2>
        ${available.length === 0
          ? '<p style="color:var(--c-ink-soft);">No rested horses available. Buy or rest a horse to race.</p>'
          : ''}
        <div class="rc-grid">${yard!.tracks.map((t) => trackCard(t, available)).join('')}</div>
      `;

      // Claim
      body.querySelectorAll<HTMLButtonElement>('button[data-claim]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.claim!;
          btn.disabled = true;
          try {
            const r = await api.claimRace(id);
            store.set({ character: r.character });
            lastResult = r;
            flash = { kind: r.placement === 1 ? 'success' : 'success', message: `${r.horseName} finished ${ordinal(r.placement)}!` };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Claim failed.' };
          } finally {
            rerender();
          }
        });
      });

      // Enter race
      body.querySelectorAll<HTMLButtonElement>('button[data-enter]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const trackSlug = btn.dataset.enter!;
          const sel = body.querySelector<HTMLSelectElement>(`select[data-horse-for="${trackSlug}"]`);
          const horseId = sel?.value;
          if (!horseId) { flash = { kind: 'warn', message: 'Pick a horse first.' }; rerender(); return; }
          const track = yard!.tracks.find((t) => t.slug === trackSlug)!;
          if (!(await showConfirmDialog({
            title: `Enter ${track.name}?`,
            message: `Entry fee: ${formatCrowns(track.entryFee)} crowns. The race runs for ${formatDuration(track.durationSeconds)}.`,
            confirmLabel: 'Enter race',
          }))) return;
          await act(() => api.enterRace(horseId, trackSlug), 'Your horse is off!', () => { tab = 'races'; });
        });
      });
    }

    function trackCard(t: RaceTrackDto, available: HorseDto[]): string {
      const w = t.weights;
      const topStat = (['speed', 'stamina', 'acceleration', 'grit'] as const)
        .map((k) => ({ k, v: w[k] }))
        .sort((a, b) => b.v - a.v)[0]!.k;
      const opts = available.map((h) => `<option value="${h.id}">${escapeHtml(h.name)} (Lv ${h.level})</option>`).join('');
      return `
        <div class="rc-card ${t.unlocked ? '' : 'locked'}">
          <img class="rc-art" src="/img/tracks/${t.slug}.webp" alt="" onerror="this.remove()" />
          <div style="display:flex; justify-content:space-between; align-items:baseline; gap:6px;">
            <h3 style="margin:0;">${escapeHtml(t.name)}</h3>
            <span class="tag">${t.tierLabel}</span>
          </div>
          <p style="font-style:italic; color:var(--c-ink-soft); font-size:0.84rem; margin:4px 0;">"${escapeHtml(t.blurb)}"</p>
          <div class="rc-tags">
            <span class="tag">${cap(t.surface)}</span>
            <span class="tag">${cap(t.distance)}</span>
            <span class="tag">favours ${shortStat(topStat)}</span>
          </div>
          <div class="rc-trackmeta">
            <span class="pill">Entry ${formatCrowns(t.entryFee)}</span>
            <span class="pill gold">Purse ${formatCrowns(t.purse)}</span>
            <span class="pill">${formatDuration(t.durationSeconds)}</span>
          </div>
          ${!t.unlocked
            ? `<button class="btn" disabled>Locked — level ${t.levelGate}</button>`
            : available.length === 0
              ? `<button class="btn" disabled>No rested horse</button>`
              : `<div style="display:flex; gap:6px;">
                   <select class="rc-select" data-horse-for="${t.slug}">${opts}</select>
                   <button class="btn" data-enter="${t.slug}">Enter</button>
                 </div>`}
        </div>
      `;
    }

    function activeRaceCard(a: ActiveRaceDto): string {
      const total = Math.max(1, Math.floor((new Date(a.endsAt).getTime() - new Date(a.startedAt).getTime()) / 1000));
      const pct = Math.min(100, Math.max(0, ((total - a.remainingSeconds) / total) * 100));
      return `
        <section class="panel" style="margin-bottom:var(--s-3);">
          <div style="display:flex; justify-content:space-between; align-items:baseline;">
            <strong>${escapeHtml(a.horseName)}</strong>
            <span style="color:var(--c-ink-soft);">${escapeHtml(a.trackName)}</span>
          </div>
          <div class="progress-bar"><div class="progress-fill" style="width:${pct.toFixed(1)}%;"></div></div>
          <div style="display:flex; gap:var(--s-2); margin-top:var(--s-2); align-items:center; flex-wrap:wrap;">
            <span class="pill ${a.ready ? 'success' : ''}">${a.ready ? 'Finished' : formatRemaining(a.remainingSeconds) + ' to go'}</span>
            <button class="btn" data-claim="${a.id}" ${a.ready ? '' : 'disabled'}>${a.ready ? 'See result' : 'Running…'}</button>
          </div>
        </section>
      `;
    }

    // ── RANKINGS ──────────────────────────────────────────────────────────────
    function renderRankingsTab(body: HTMLDivElement): void {
      if (!rankings) { body.innerHTML = '<p>Loading…</p>'; return; }
      const riders = rankings.topRiders;
      const horses = rankings.topHorses;
      body.innerHTML = `
        <h2 class="rc-h2">Top Riders — Racing Prestige</h2>
        ${riders.length === 0 ? '<p style="color:var(--c-ink-soft);">No prestige earned yet. Be the first.</p>' : `
          <table class="rc-table">
            <thead><tr><th>#</th><th>Rider</th><th>Prestige</th><th>Level</th></tr></thead>
            <tbody>${riders.map((r, i) => `
              <tr><td>${i + 1}</td>
                  <td><a href="#/u/${encodeURIComponent(r.characterId)}">${escapeHtml(r.name)}</a></td>
                  <td>${r.racingPrestige}</td><td>${r.level}</td></tr>`).join('')}</tbody>
          </table>`}
        <h2 class="rc-h2">Winningest Horses</h2>
        ${horses.length === 0 ? '<p style="color:var(--c-ink-soft);">No wins recorded yet.</p>' : `
          <table class="rc-table">
            <thead><tr><th>#</th><th>Horse</th><th>Breed</th><th>Wins</th><th>Owner</th></tr></thead>
            <tbody>${horses.map((h, i) => `
              <tr><td>${i + 1}</td>
                  <td>${escapeHtml(h.name)}</td>
                  <td>${escapeHtml(h.breedName)} <span class="tag">${h.tierLabel}</span></td>
                  <td>${h.wins}/${h.races}</td>
                  <td><a href="#/u/${encodeURIComponent(h.ownerId)}">${escapeHtml(h.ownerName)}</a></td></tr>`).join('')}</tbody>
          </table>`}
      `;
    }

    // ── shared action wrapper ──────────────────────────────────────────────────
    async function act(fn: () => Promise<unknown>, okMsg: string, after?: () => void): Promise<void> {
      try {
        await fn();
        flash = { kind: 'success', message: okMsg };
        after?.();
        await refresh();
      } catch (err) {
        flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Action failed.' };
      } finally {
        rerender();
      }
    }

    anchored(renderMain);
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// View helpers
// ──────────────────────────────────────────────────────────────────────────────

function renderResultBanner(r: ClaimRaceResponse): string {
  const podium = r.field.slice(0, 5).map((f) =>
    `${f.placement}. ${escapeHtml(f.name)}${f.isPlayer ? ' <strong>(you)</strong>' : f.isGhost ? '' : ` <span style="color:var(--c-ink-soft);">(${escapeHtml(f.ownerName)})</span>`}`,
  ).join('<br/>');
  const lvl = r.levelsGained > 0 ? `, <strong>+${r.levelsGained} level${r.levelsGained === 1 ? '' : 's'}</strong>` : '';
  const horseLvl = r.horseLeveledTo ? ` Your horse reached level ${r.horseLeveledTo}.` : '';
  return `
    <div class="alert ${r.placement === 1 ? 'success' : ''}" style="margin-bottom:var(--s-3);">
      <strong>${escapeHtml(r.horseName)} finished ${ordinal(r.placement)} of ${r.fieldSize} at ${escapeHtml(r.trackName)}.</strong><br/>
      +${formatCrowns(r.crownsGained)} crowns, +${formatBigNum(r.riderXpGained)} XP, +${r.prestigeGained} prestige${lvl}.${horseLvl}
      <details style="margin-top:6px;"><summary style="cursor:pointer;">Full finishing order</summary>
        <div style="margin-top:6px; font-size:0.9rem; line-height:1.5;">${podium}</div>
      </details>
    </div>
  `;
}

function statBlock(s: { speed: number; stamina: number; acceleration: number; grit: number }): string {
  const row = (label: string, v: number) =>
    `<div class="rc-stat"><span>${label}</span><span class="rc-statbar"><span style="width:${Math.min(100, v / 2)}%"></span></span><b>${v}</b></div>`;
  return `<div class="rc-stats">
    ${row('SPD', s.speed)}${row('STM', s.stamina)}${row('ACC', s.acceleration)}${row('GRT', s.grit)}
  </div>`;
}

function xpBar(h: HorseDto): string {
  if (h.xpForNext <= 0) return '';
  const pct = Math.min(100, (h.xpIntoLevel / h.xpForNext) * 100);
  return `<div class="rc-xp"><div class="rc-xp-fill" style="width:${pct.toFixed(0)}%"></div></div>`;
}

function gearLine(h: HorseDto): string {
  const items = [h.gear.saddle, h.gear.shoes, h.gear.barding, h.gear.charm].filter(Boolean) as Array<{ name: string }>;
  if (items.length === 0) return '<span style="color:var(--c-ink-soft); font-size:0.78rem;">No gear fitted</span>';
  return items.map((g) => `<span class="tag">${escapeHtml(g.name)}</span>`).join(' ');
}

function shortStat(k: string): string {
  return { speed: 'speed', stamina: 'stamina', acceleration: 'acceleration', grit: 'grit' }[k] ?? k;
}
function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

function formatRemaining(seconds: number): string {
  if (seconds <= 0) return 'now';
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), sec = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}
function formatDuration(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m >= 60) { const h = Math.floor(m / 60), rem = m % 60; return rem ? `${h}h ${rem}m` : `${h}h`; }
  return `${m} min`;
}

function buildStyle(): HTMLStyleElement {
  const style = document.createElement('style');
  style.textContent = `
    .rc-tabs { display:flex; gap:6px; border-bottom:1px solid var(--c-rule); margin:var(--s-3) 0; flex-wrap:wrap; }
    .rc-tab { background:none; border:none; padding:8px 16px; font-family:var(--font-display); font-size:1rem;
      cursor:pointer; color:var(--c-ink-soft); border-bottom:2px solid transparent; }
    .rc-tab.active { color:var(--c-oxblood); border-bottom-color:var(--c-oxblood); }
    .rc-h2 { font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule);
      padding-bottom:var(--s-1); margin:var(--s-4) 0 var(--s-3); }
    .rc-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(min(280px,100%),1fr)); gap:var(--s-3); }
    .rc-card { background:var(--c-parchment); border:1px solid var(--c-rule); border-radius:var(--radius);
      padding:var(--s-3); display:flex; flex-direction:column; gap:6px; overflow:hidden; }
    .rc-card.locked { opacity:0.6; }
    .rc-art { display:block; width:calc(100% + 2 * var(--s-3)); margin:calc(-1 * var(--s-3)) calc(-1 * var(--s-3)) 2px;
      aspect-ratio:21 / 9; object-fit:cover; border-bottom:1px solid var(--c-rule-soft); }
    .rc-gear-emblem { width:100px; height:100px; object-fit:contain; vertical-align:middle; margin-right:10px;
      border-radius:var(--radius); }
    .rc-tags { display:flex; gap:5px; flex-wrap:wrap; }
    .tag { background:var(--c-parchment-dark); border:1px solid var(--c-rule-soft); border-radius:3px;
      padding:1px 7px; font-size:0.76rem; font-family:var(--font-numeric); }
    .pill { background:var(--c-parchment-dark); border:1px solid var(--c-rule-soft); border-radius:999px;
      padding:2px 10px; font-family:var(--font-numeric); font-size:0.85rem; }
    .pill.success { border-color:var(--c-success); color:var(--c-success); }
    .pill.warn { border-color:var(--c-gold); color:var(--c-gold-text, var(--c-gold)); }
    .rc-stats { display:flex; flex-direction:column; gap:2px; margin:4px 0; }
    .rc-stat { display:grid; grid-template-columns:34px 1fr 34px; gap:6px; align-items:center; font-size:0.78rem; font-family:var(--font-numeric); }
    .rc-statbar { background:var(--c-parchment-dark); border-radius:3px; height:7px; overflow:hidden; }
    .rc-statbar > span { display:block; height:100%; background:linear-gradient(90deg,var(--c-gold),var(--c-gold-bright)); }
    .rc-stat b { text-align:right; }
    .rc-xp { height:5px; background:var(--c-parchment-dark); border-radius:3px; overflow:hidden; }
    .rc-xp-fill { height:100%; background:var(--c-oxblood); }
    .rc-gearline { display:flex; gap:4px; flex-wrap:wrap; min-height:18px; }
    .rc-trackmeta { display:flex; gap:6px; flex-wrap:wrap; margin:4px 0; }
    /* Colour/background/padding come from the global .rc-select rule in
       pages.css (cream cell + dark ink in BOTH themes). Overriding background
       here with var(--c-parchment) made the control dark-on-dark in dark mode
       — only layout belongs in this page-level rule. */
    .rc-select { flex:1; min-width:0; }
    .btn.sm { padding:4px 10px; font-size:0.82rem; }
    .rc-gearpanel { border-top:1px solid var(--c-rule-soft); margin-top:8px; padding-top:8px; display:flex; flex-direction:column; gap:8px; }
    .rc-gearslot-h { font-family:var(--font-display); font-size:0.85rem; margin-bottom:4px; }
    .rc-gearopts { display:flex; flex-direction:column; gap:4px; }
    .rc-gearopt { display:flex; justify-content:space-between; align-items:center; gap:8px;
      background:var(--c-parchment-dark); border:1px solid var(--c-rule-soft); border-radius:var(--radius); padding:6px 8px; }
    .rc-gearopt.blocked { opacity:0.55; }
    .rc-table { width:100%; border-collapse:collapse; font-size:0.9rem; }
    .rc-table th, .rc-table td { text-align:left; padding:6px 8px; border-bottom:1px solid var(--c-rule-soft); }
    .rc-table th { font-family:var(--font-display); color:var(--c-oxblood); }
    .progress-bar { background:var(--c-parchment-dark); border:1px solid var(--c-rule); border-radius:var(--radius); height:16px; overflow:hidden; }
    .progress-fill { background:linear-gradient(90deg,var(--c-gold),var(--c-gold-bright)); height:100%; transition:width 0.5s; }
  `;
  return style;
}
